import React, { useState, useEffect } from 'react';
import {
  AlertCircle,
  MessageSquare,
  FileEdit,
  CheckCircle,
  X,
  ChevronRight,
  Loader2,
  Edit3,
  SkipForward,
  Sparkles
} from 'lucide-react';
import { getDocumentGaps, applyGapFill, type DocumentGap, type GapSuggestion } from '../api';
import toast from 'react-hot-toast';

interface GapFillingWorkflowProps {
  documentId: number;
  onComplete: () => void;
  onCancel: () => void;
}

const priorityColors: Record<string, { bg: string; text: string; border: string }> = {
  high: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  medium: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
  low: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' }
};

const sourceTypeIcons: Record<string, React.ReactNode> = {
  interview: <MessageSquare className="w-4 h-4 text-purple-500" />,
  change_detection: <FileEdit className="w-4 h-4 text-blue-500" />,
  ai_inference: <Sparkles className="w-4 h-4 text-yellow-500" />,
  manual: <Edit3 className="w-4 h-4 text-gray-500" />
};

const GapFillingWorkflow: React.FC<GapFillingWorkflowProps> = ({
  documentId,
  onComplete,
  onCancel
}) => {
  const [gaps, setGaps] = useState<DocumentGap[]>([]);
  const [currentGapIndex, setCurrentGapIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<GapSuggestion | null>(null);
  const [manualInput, setManualInput] = useState('');
  const [isManualMode, setIsManualMode] = useState(false);
  const [completedGaps, setCompletedGaps] = useState<string[]>([]);
  const [skippedGaps, setSkippedGaps] = useState<string[]>([]);

  useEffect(() => {
    loadGaps();
  }, [documentId]);

  const loadGaps = async () => {
    try {
      setIsLoading(true);
      const response = await getDocumentGaps(documentId);
      setGaps(response.gaps || []);
    } catch (error) {
      console.error('Error loading gaps:', error);
      toast.error('Failed to load gap suggestions');
    } finally {
      setIsLoading(false);
    }
  };

  const currentGap = gaps[currentGapIndex];
  const totalGaps = gaps.length;
  const processedGaps = completedGaps.length + skippedGaps.length;
  const progress = totalGaps > 0 ? (processedGaps / totalGaps) * 100 : 0;

  const handleAcceptSuggestion = async (suggestion: GapSuggestion) => {
    if (!currentGap) return;

    try {
      setIsSubmitting(true);
      await applyGapFill(documentId, currentGap.gap_id, {
        fillText: suggestion.suggested_text,
        sourceType: suggestion.source_type,
        sourceId: suggestion.source_id || undefined,
        sourceAttribution: suggestion.source_attribution,
        confidenceScore: suggestion.confidence_score
      });

      setCompletedGaps(prev => [...prev, currentGap.gap_id]);
      toast.success('Gap filled successfully');
      moveToNextGap();
    } catch (error) {
      console.error('Error applying gap fill:', error);
      toast.error('Failed to apply gap fill');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleManualSubmit = async () => {
    if (!currentGap || !manualInput.trim()) return;

    try {
      setIsSubmitting(true);
      await applyGapFill(documentId, currentGap.gap_id, {
        fillText: manualInput.trim(),
        sourceType: 'manual',
        sourceAttribution: 'Manual input',
        confidenceScore: 100
      });

      setCompletedGaps(prev => [...prev, currentGap.gap_id]);
      setManualInput('');
      setIsManualMode(false);
      toast.success('Gap filled successfully');
      moveToNextGap();
    } catch (error) {
      console.error('Error applying manual gap fill:', error);
      toast.error('Failed to apply gap fill');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    if (!currentGap) return;
    setSkippedGaps(prev => [...prev, currentGap.gap_id]);
    moveToNextGap();
  };

  const moveToNextGap = () => {
    setSelectedSuggestion(null);
    setManualInput('');
    setIsManualMode(false);

    if (currentGapIndex < totalGaps - 1) {
      setCurrentGapIndex(prev => prev + 1);
    } else {
      // All gaps processed
      onComplete();
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-lg w-full mx-4">
          <div className="flex flex-col items-center">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-4" />
            <p className="text-gray-600">Loading gap suggestions...</p>
          </div>
        </div>
      </div>
    );
  }

  if (gaps.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-lg w-full mx-4">
          <div className="flex flex-col items-center">
            <CheckCircle className="w-12 h-12 text-green-500 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Gaps Found</h3>
            <p className="text-gray-600 text-center mb-6">
              This document appears to be complete. No gaps were identified during assessment.
            </p>
            <button
              onClick={onComplete}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  const colors = priorityColors[currentGap?.priority || 'medium'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Gap Filling Workflow</h2>
            <p className="text-sm text-gray-500">
              Fill in missing information to enhance your document
            </p>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress */}
        <div className="px-4 py-3 bg-gray-50 border-b">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">
              Gap {currentGapIndex + 1} of {totalGaps}
            </span>
            <span className="text-sm text-gray-600">
              {completedGaps.length} filled, {skippedGaps.length} skipped
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Current Gap */}
        {currentGap && (
          <div className="flex-1 overflow-y-auto p-4">
            {/* Gap Info */}
            <div className={`p-4 rounded-lg border ${colors.border} ${colors.bg} mb-4`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <AlertCircle className={`w-5 h-5 ${colors.text}`} />
                  <span className={`font-medium ${colors.text}`}>
                    {currentGap.gap_type.replace(/_/g, ' ')}
                  </span>
                </div>
                <span className={`text-xs px-2 py-1 rounded ${colors.text} ${colors.bg} border ${colors.border}`}>
                  {currentGap.priority} priority
                </span>
              </div>
              <p className="text-gray-700">{currentGap.description}</p>
              {currentGap.suggested_fill && (
                <p className="mt-2 text-sm text-gray-500 italic">
                  Hint: {currentGap.suggested_fill}
                </p>
              )}
            </div>

            {/* Suggestions or Manual Input */}
            {!isManualMode ? (
              <>
                {currentGap.has_auto_suggestions && currentGap.suggestions.length > 0 ? (
                  <div className="space-y-3">
                    <h4 className="font-medium text-gray-700">Suggested Fills:</h4>
                    {currentGap.suggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          selectedSuggestion === suggestion
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setSelectedSuggestion(suggestion)}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            {sourceTypeIcons[suggestion.source_type]}
                            <span className="text-sm text-gray-600">
                              {suggestion.source_attribution}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-400">
                              Relevance: {suggestion.relevance_score}%
                            </span>
                            <span className="text-xs text-gray-400">
                              Confidence: {suggestion.confidence_score}%
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-800 line-clamp-3">
                          {suggestion.suggested_text}
                        </p>
                        {selectedSuggestion === suggestion && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAcceptSuggestion(suggestion);
                            }}
                            disabled={isSubmitting}
                            className="mt-3 w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center space-x-2"
                          >
                            {isSubmitting ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4" />
                                <span>Accept This Suggestion</span>
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    ))}

                    <button
                      onClick={() => setIsManualMode(true)}
                      className="w-full py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 flex items-center justify-center space-x-2"
                    >
                      <Edit3 className="w-4 h-4" />
                      <span>Enter Manually Instead</span>
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="p-4 bg-gray-50 rounded-lg text-center">
                      <p className="text-gray-600 mb-3">
                        No automatic suggestions found for this gap.
                      </p>
                      <button
                        onClick={() => setIsManualMode(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        Enter Information Manually
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-3">
                <h4 className="font-medium text-gray-700">Manual Input:</h4>
                <textarea
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  placeholder="Enter the information to fill this gap..."
                  className="w-full h-32 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <div className="flex space-x-3">
                  <button
                    onClick={() => setIsManualMode(false)}
                    className="flex-1 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
                  >
                    Back to Suggestions
                  </button>
                  <button
                    onClick={handleManualSubmit}
                    disabled={!manualInput.trim() || isSubmitting}
                    className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center space-x-2"
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        <span>Submit</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
          <button
            onClick={handleSkip}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
          >
            <SkipForward className="w-4 h-4" />
            <span>Skip This Gap</span>
          </button>

          <div className="flex items-center space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={onComplete}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
            >
              <span>Finish</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GapFillingWorkflow;
